import { EitherOr } from '@qr-front/ui/utils'
import {
	socialLinkListAtom,
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from 'features/generator/contentFormSections'
import {
	businessInfoButtonListAtom,
	businessInfoCompanyNameAtom,
	businessInfoSubtitleAtom,
	businessInfoTitleAtom,
} from 'features/generator/contentFormSections/BusinessInfoForm'
import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
	normalizeEmailEntries,
	normalizePhoneEntries,
	serializeContactEntries,
	TContactField,
} from 'features/generator/contentFormSections/CommonSections/ContactSection'
import { contactInfoFullNameAtom } from 'features/generator/contentFormSections/ContactInfoForm/ContactInfoForm'
import { facilitiesAtom } from 'features/generator/contentFormSections/FacilitiesForm/FacilitiesForm'
import { imageFormAtom } from 'features/generator/contentFormSections/ImageForm'
import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
	addressUrlAtom,
} from 'features/generator/contentFormSections/LocationForm/addressAtoms'
import {
	openingHoursStateAtom,
	sharedOpeningHoursStateAtom,
	weekDays,
	weekDaysAreSharedAtom,
} from 'features/generator/contentFormSections/OpeningHoursForm/openingHoursState'
import { TOpeningHoursState } from 'features/generator/contentFormSections/OpeningHoursForm/openingHoursTypes'
import { selectedTabAtom } from 'features/generator/contentFormSections/OpeningHoursForm/TabNavigation'
import { summaryAtom } from 'features/generator/contentFormSections/SummaryForm/SummarySection'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { SOCIAL_PLATFORM_LIST } from 'features/generator/utils/socialPlatformsConfig'
import { atom } from 'jotai'
import { TUploadedMedia } from 'services/mediaStorage'
import { isOnServer } from 'utils/generalUtils'
import { getFileFromMedia } from 'utils/getFileFromMedia'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'

import { businessPagePrimaryColorAtom } from './BusinessPageScreen/BusinessPageScreenState'

export type TBusinessContent = {
	type: QR_TYPE.BusinessPage
	colors: Omit<TQrColorScheme, 'name'>
	address: EitherOr<
		{ url: string },
		{
			city: string
			country: string
			postalCode: string
			state: string
			street: string
		}
	>
	links: {
		name: string
		url: string | undefined
	}[]
	buttons: { title: string; url: string | undefined }[]
	contacts: {
		phoneNumber: TContactField
		alternativePhoneNumber?: string
		website: string
		email: TContactField
	}
	contactDetails: {
		name: string
		surname: string
	}
	company: { name: string; tagline: string; summary: string }
	summary: string
	facilities: string[]
	schedule: TOpeningHoursState
	image: TUploadedMedia
	loaderImage: TUploadedMedia
}

export const businessContentAtom = atom<
	TBusinessContent,
	[TBusinessContent],
	void
>(
	(get) => ({
		type: QR_TYPE.BusinessPage,
		colors: {
			primary: get(businessPagePrimaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		address: get(addressUrlAtom)
			? { url: get(addressUrlAtom) || '' }
			: {
					street: get(addressStreetAtom) || '',
					postalCode: get(addressPostalCodeAtom) || '',
					city: get(addressCityAtom) || '',
					state: get(addressStateAtom) || '',
					country: get(addressCountryAtom) || '',
				},
		links: get(socialLinkListAtom).map(({ name, url }) => ({
			name,
			url,
		})),
		buttons: get(businessInfoButtonListAtom),
		company: {
			name: get(businessInfoCompanyNameAtom),
			tagline: get(businessInfoTitleAtom) || '',
			summary: get(businessInfoSubtitleAtom) || '',
		},
		image:
			get(imageFormAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
		contacts: {
			phoneNumber: serializeContactEntries(get(contactInfoPhoneListAtom)),
			alternativePhoneNumber: '',
			website: get(contactInfoWebsiteAtom) || '',
			email: serializeContactEntries(get(contactInfoEmailListAtom)),
		},
		contactDetails: {
			name: get(contactInfoFullNameAtom) || '',
			surname: '',
		},
		summary: get(summaryAtom) || '',
		facilities: get(facilitiesAtom),
		schedule:
			get(selectedTabAtom) === 'open-24h'
				? {
						isAm: false,
						isOpen247: true,
						monday: null,
						tuesday: null,
						wednesday: null,
						thursday: null,
						friday: null,
						saturday: null,
						sunday: null,
					}
				: get(weekDaysAreSharedAtom)
					? get(sharedOpeningHoursStateAtom)
					: get(openingHoursStateAtom),
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content: TBusinessContent) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		// Address
		if (content.address?.url) {
			set(addressUrlAtom, content.address.url)
		} else {
			set(addressStreetAtom, content.address.street)
			set(addressPostalCodeAtom, content.address.postalCode)
			set(addressCityAtom, content.address.city)
			set(addressStateAtom, content.address.state)
			set(addressCountryAtom, content.address.country)
		}
		// Buttons
		set(businessInfoButtonListAtom, content.buttons)
		// Business Info
		set(businessInfoCompanyNameAtom, content.company.name)
		set(businessInfoTitleAtom, content.company.tagline)
		set(businessInfoSubtitleAtom, content.company.summary)
		// Image
		set(imageFormAtom, content.image)
		// Contact
		set(
			contactInfoPhoneListAtom,
			normalizePhoneEntries(
				content.contacts.phoneNumber,
				content.contacts.alternativePhoneNumber,
			),
		)
		set(contactInfoWebsiteAtom, content.contacts.website)
		set(
			contactInfoEmailListAtom,
			normalizeEmailEntries(content.contacts.email),
		)
		// Contact Details
		const fullName = [
			content.contactDetails.name,
			content.contactDetails.surname,
		]
			.filter(Boolean)
			.join(' ')
		set(contactInfoFullNameAtom, fullName)
		// Summary
		set(summaryAtom, content.summary)
		// Facilities
		set(facilitiesAtom, content.facilities)
		// Opening Hours
		if (content.schedule.isOpen247) {
			set(selectedTabAtom, 'open-24h')
		} else if (!content.schedule.isAm) {
			set(selectedTabAtom, '24h')
		} else if (content.schedule.isAm) {
			set(selectedTabAtom, 'am-pm')
		}
		if (!content.schedule.isOpen247) {
			const weekdays = weekDays.slice(0, 5)
			const weekendDays = weekDays.slice(5)

			const weekDaysAreShared =
				weekdays.every((day) => content.schedule[day]?.isActive) &&
				weekendDays.every((day) => !content.schedule[day]?.isActive) &&
				weekdays.every((day) => {
					const ranges = content.schedule[day]?.dateRanges
					if (!ranges) return false

					const firstDay = weekdays[0]
					if (!firstDay) return false

					const firstDayRanges =
						content.schedule[firstDay]?.dateRanges

					if (ranges.length !== firstDayRanges?.length) return false

					return ranges.every((range, i) => {
						const rangeStart = range?.start?.toString()
						const firstDayStart =
							firstDayRanges[i]?.start?.toString()
						return rangeStart === firstDayStart
					})
				})

			if (weekDaysAreShared) {
				set(weekDaysAreSharedAtom, true)
				set(sharedOpeningHoursStateAtom, content.schedule)
			} else {
				set(weekDaysAreSharedAtom, false)
				set(openingHoursStateAtom, content.schedule)
			}
		}
		// Social links
		const match = (name: string) =>
			SOCIAL_PLATFORM_LIST.find(
				(platform) =>
					platform.name.toLowerCase() === name.toLowerCase(),
			)
		const filteredLinks = content.links.filter(({ name }) => match(name))
		const socialLinks = filteredLinks.map(({ name, url }) => ({
			platformId: match(name)!.id,
			name,
			url,
		}))
		set(socialLinkListAtom, socialLinks)
		// Welcome screen
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(
						content.loaderImage,
					)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
